import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { selectCurrentGroupId } from './groups_slice';
import { parseTimespanFilter, parseAgeFilter } from '../../_helpers/misc_functions';
import { fetchData } from './api';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AsyncDataSlot {
  data: unknown;
  status: AsyncStatus;
  error: string | null;
}

interface EngagementFilters {
  timespan: string;
  start_date: string | null;
  end_date: string | null;
  age: string;
  gender: string;
  department: string;
  role: string;
  specialty: string;
  years_in_profession: string;
  [key: string]: unknown;
}

interface EngagementState {
  userMetrics: {
    filters: EngagementFilters;
    usersRegistered: AsyncDataSlot;
    microlearningsWatched: AsyncDataSlot;
  };
}

const initialState: EngagementState = {
  userMetrics: {
    filters: {
      timespan: 'Custom',
      start_date: parseTimespanFilter({ timespan: '90 Days' }).start_date,
      end_date: parseTimespanFilter({ timespan: '90 Days' }).end_date,
      age: 'All',
      gender: 'All',
      department: 'All',
      role: 'All',
      specialty: 'All',
      years_in_profession: 'All',
    },
    usersRegistered: { data: null, status: 'idle', error: null },
    microlearningsWatched: { data: null, status: 'idle', error: null },
  },
};

export const fetchUsersRegistered = createAsyncThunk<unknown, void, { state: RootState }>(
  'engagement/fetchUsersRegistered',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    let filters = state.engagement.userMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    filters = { ...filters, start_date, end_date, start_age, end_age, practice_group_id } as any;
    try {
      return await fetchData({ endpoint: 'insights/engagement/users_registered', filters: filters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch engagement users registered');
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState().engagement.userMetrics.usersRegistered.status;
      return status !== 'loading';
    },
  }
);

export const fetchViewsPerSession = createAsyncThunk<unknown, void, { state: RootState }>(
  'engagement/fetchViewsPerSession',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const practice_group_id = selectCurrentGroupId(state);
    let filters = state.engagement.userMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters);
    const { start_age, end_age } = parseAgeFilter(filters.age);
    filters = { ...filters, start_date, end_date, start_age, end_age, practice_group_id } as any;
    try {
      return await fetchData({ endpoint: 'insights/engagement/microlearnings', filters: filters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch engagement views per session');
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState().engagement.userMetrics.microlearningsWatched.status;
      return status !== 'loading';
    },
  }
);

const engagementSlice = createSlice({
  name: 'engagement',
  initialState,
  reducers: {
    resetEngagementData: () => initialState,
    setEngagementUserFilters: (state, action: PayloadAction<Partial<EngagementFilters>>) => {
      state.userMetrics.filters = { ...state.userMetrics.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersRegistered.pending, (state) => {
        state.userMetrics.usersRegistered.status = 'loading';
        state.userMetrics.usersRegistered.error = null;
      })
      .addCase(fetchUsersRegistered.fulfilled, (state, action) => {
        state.userMetrics.usersRegistered.status = 'succeeded';
        state.userMetrics.usersRegistered.data = action.payload;
      })
      .addCase(fetchUsersRegistered.rejected, (state, action) => {
        state.userMetrics.usersRegistered.status = 'failed';
        state.userMetrics.usersRegistered.error = (action.payload as string) ?? action.error.message ?? null;
      })

      .addCase(fetchViewsPerSession.pending, (state) => {
        state.userMetrics.microlearningsWatched.status = 'loading';
        state.userMetrics.microlearningsWatched.error = null;
      })
      .addCase(fetchViewsPerSession.fulfilled, (state, action) => {
        state.userMetrics.microlearningsWatched.status = 'succeeded';
        state.userMetrics.microlearningsWatched.data = action.payload;
      })
      .addCase(fetchViewsPerSession.rejected, (state, action) => {
        state.userMetrics.microlearningsWatched.status = 'failed';
        state.userMetrics.microlearningsWatched.error = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { resetEngagementData, setEngagementUserFilters } = engagementSlice.actions;
export default engagementSlice.reducer;
